import {
  ConformityLevel,
  ImportExportLevel,
  ImportExportOption,
  ProfileSelectionsDto,
  TestCaseDto,
  TestCaseProfileDto,
} from "../autogenerated/models";
import EnumHelpers from "./enumHelpers";

export enum ProfileField {
  IndividualImportOption,
  IndividualExportOption,
  InclusiveImportOption,
  InclusiveExportOption,
  DifferenceImportOption,
  DifferenceExportOption,
}

export const getProfileConformityLevel = (
  profileSelection: ProfileSelectionsDto,
  importExportLevels: ImportExportLevel[]
) => {
  if (
    profileSelection.inclusiveOption?.selection === ImportExportOption.Yes &&
    profileSelection.individualOption?.selection === ImportExportOption.Yes &&
    (!importExportLevels.some(
      (ieLevel) => ieLevel === ImportExportLevel.Difference
    ) ||
      profileSelection.differenceOption?.selection === ImportExportOption.Yes)
  )
    return ConformityLevel.Gold;
  if (
    (profileSelection.inclusiveOption?.selection === ImportExportOption.Yes &&
      profileSelection.individualOption?.selection ===
        ImportExportOption.Yes) ||
    (profileSelection.inclusiveOption?.selection === ImportExportOption.Yes &&
      profileSelection.differenceOption?.selection ===
        ImportExportOption.Yes) ||
    (profileSelection.individualOption?.selection === ImportExportOption.Yes &&
      profileSelection.differenceOption?.selection === ImportExportOption.Yes)
  )
    return ConformityLevel.Silver;
  if (
    profileSelection.inclusiveOption?.selection === ImportExportOption.Yes ||
    profileSelection.individualOption?.selection === ImportExportOption.Yes ||
    profileSelection.differenceOption?.selection === ImportExportOption.Yes
  )
    return ConformityLevel.Bronze;

  return ConformityLevel.None;
};

export const minImportLevelForProfiles = (profiles: TestCaseProfileDto[]) => {
  let importLevels: Array<ConformityLevel> = [];
  profiles.forEach((p) => {
    importLevels.push(
      getProfileConformityLevel(
        p.importSelections ?? {},
        p.importExportLevels ?? []
      )
    );
  });

  let minImport = Math.min(
    ...importLevels
      .filter((il) => il !== ConformityLevel.None)
      .map((il) => il as number)
  );
  minImport = minImport !== Infinity ? minImport : 0;
  return minImport as ConformityLevel;
};

export const minExportLevelForProfiles = (profiles: TestCaseProfileDto[]) => {
  let exportLevels: Array<ConformityLevel> = [];
  profiles.forEach((p) => {
    exportLevels.push(
      getProfileConformityLevel(
        p.exportSelections ?? {},
        p.importExportLevels ?? []
      )
    );
  });

  let minExport = Math.min(
    ...exportLevels
      .filter((el) => el !== ConformityLevel.None)
      .map((el) => el as number)
  );
  minExport = minExport !== Infinity ? minExport : 0;
  return minExport as ConformityLevel;
};

export const allFilesUploaded = (testCase: TestCaseDto) => {
  let result = true;
  if (
    testCase.testSteps?.every((ts) =>
      ts.testFiles?.every((tf) => tf.internalEvaluation)
    )
  ) {
    testCase.testSteps
      ?.filter((testStep) => testStep.datasets?.length !== 0)
      .forEach((testStep) => {
        testStep.datasets?.forEach((dataset) => {
          if (
            !testStep.testFiles?.some(
              (testFile) => testFile.testDatasetId === dataset.id
            )
          )
            result = false;
        });
      });
  } else {
    testCase.testConfigurations?.forEach((testConfiguration) => {
      testCase.testSteps
        ?.filter((testStep) => testStep.datasets?.length !== 0)
        .forEach((testStep) => {
          testStep.datasets?.forEach((dataset) => {
            if (
              !testStep.testFiles?.some(
                (testFile) =>
                  testFile.testConfigurationId === testConfiguration.id &&
                  testFile.testDatasetId === dataset.id
              )
            )
              result = false;
          });
        });
    });
  }

  return result;
};

export const getProfileOption = (
  field: ProfileField,
  profile: TestCaseProfileDto | undefined,
) => {
  switch (field) {
    case ProfileField.InclusiveImportOption:
      return profile?.importSelections?.inclusiveOption;
    case ProfileField.InclusiveExportOption:
      return profile?.exportSelections?.inclusiveOption;
    case ProfileField.IndividualImportOption:
      return profile?.importSelections?.individualOption;
    case ProfileField.IndividualExportOption:
      return profile?.exportSelections?.individualOption;
    case ProfileField.DifferenceImportOption:
      return profile?.importSelections?.differenceOption;
    case ProfileField.DifferenceExportOption:
      return profile?.exportSelections?.differenceOption;
    default:
      return undefined;
  }
};

export const getProfileFieldValue = (
  field: ProfileField,
  profile: TestCaseProfileDto | undefined
) => getProfileOption(field, profile)?.selection ?? ImportExportOption.No;

export const getProfileFieldUnmetDependencies = (
  field: ProfileField,
  profile: TestCaseProfileDto
) => getProfileOption(field, profile)?.unmetDependencies ?? [];

export const getProfileFieldDependencyValue = (
  field: ProfileField,
  profiles: TestCaseProfileDto[],
  dependencyId: string
) => {
  const dependencies = profiles?.find(p => p.id === dependencyId);
  return getProfileFieldValue(field, dependencies);
};

export const isSelected = (selection: ImportExportOption | undefined) =>
  selection === ImportExportOption.Yes

export const isDeselected = (selection: ImportExportOption | undefined) =>
  selection !== ImportExportOption.Yes

export const getDependenciesOfProfile = (
  profile: TestCaseProfileDto,
  allProfiles: TestCaseProfileDto[] | undefined
) => allProfiles?.filter(p => profile.profileDependencies?.some(d => d === p.id)) ?? []

export const getUnmetDependencies = (
  field: ProfileField,
  dependencies: TestCaseProfileDto[]
) => dependencies?.filter(d => isDeselected(getProfileFieldValue(field, d)));

export const getUnmetProfileDependencies = (
  field: ProfileField,
  profile: TestCaseProfileDto,
  profiles: TestCaseProfileDto[]
) =>
  isSelected(getProfileFieldValue(field, profile))
    ? profile.profileDependencies?.filter(
        pd => isDeselected(getProfileFieldDependencyValue(field, profiles, pd))
      ) ?? []
    : [];

export const hasUnmetDependencies = (profile: TestCaseProfileDto) =>
  EnumHelpers.getValues(ProfileField).some(
    (field) =>
      isSelected(getProfileFieldValue(field, profile)) &&
      (getProfileFieldUnmetDependencies(field, profile)?.length ?? 0) > 0
  );

export const isIndividualOrInclusiveNeeded = (
  field: ProfileField,
  profile: TestCaseProfileDto | undefined
) => {
  const isDeselectedProfile = (
    f: ProfileField
  ) => isDeselected(getProfileFieldValue(f, profile))
  switch (field) {
    case ProfileField.DifferenceImportOption:
      return isDeselectedProfile(ProfileField.IndividualImportOption) &&
        isDeselectedProfile(ProfileField.InclusiveImportOption);
    case ProfileField.DifferenceExportOption:
      return isDeselectedProfile(ProfileField.IndividualExportOption) &&
        isDeselectedProfile(ProfileField.InclusiveExportOption);
    default:
      return false
  }
}
